import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import StatusLegend from '../../Common/StatusLegend/StatusLegend';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../../Common/Notification';
import Button from '../../Common/Button';
import Modal61 from '../../Common/Modal/Modal61';
import ProjectsWidgetDrawer from './ProjectsWidgetDrawer';
import { actions as companyDashboardActions } from '../../../actions/companyDashboardActions';
import { withAbortableApi } from '@Common/AbortableApi';
import { TruncateText } from '../../../helpers/strings';
import './Projects.scss';
import RadialBarGraph from '../../Common/RadialBarGraph/RadialBarGraph';
import { withRouter } from 'react-router-dom';
import * as types from '../../../constants/actionTypes';
import Routes from '../../../constants/routes';
import { MOBILE_WINDOW_BREAKPOINT } from '../../../constants/constants';
import { CUSTOMER_ROLES } from '../../../constants/roles';

export class Projects extends Component {
	constructor(props, context) {
		super(props, context);
		this._isMounted = false;

		this.state = {
			isLoading: true,
			isDrawerOpen: false,
			showMore: false,
			projects: [],
			selectedProject: null,
			pageSize: 7,
			pageNumber: 1,
			isMobile: this.props.windowWidth < MOBILE_WINDOW_BREAKPOINT,
			history: PropTypes.object.isRequired,
		};

		this.handleResize = this.handleResize.bind(this);

		this.permissionsModal = createRef();
	}

	componentDidMount = async () => {
		this._isMounted = true;
		window.addEventListener('resize', this.handleResize);
		await this.loadProjects(false);
	};

	componentDidUpdate = async (prevProps) => {
		if (this.props.settings && this.props.settings !== prevProps.settings) {
			this.setState({ pageNumber: 1 }, async () => await this.loadProjects(true));
		}
	};

	componentWillUnmount() {
		this._isMounted = false;
		window.removeEventListener('resize', this.handleResize);
	}

	loadProjects = async (isNewFilters) => {
		if (this._isMounted) this.setState({ isLoading: true });

		try {
			const { ...request } = this.props.settings;
			const response = await this.props.callAbortableApi((signal) => this.props.companyDashboardActions.loadProjects(request, this.state.pageSize, this.state.pageNumber, signal));
			if (this._isMounted && response.type === types.LOAD_COMPANY_DASHBOARD_PROJECTS_SUCCESS) {
				const showMore = response.payload.totalCount < this.state.pageSize ? false : true;

				if (response.payload.results) {
					this.setState({
						projects: isNewFilters === true ? response.payload.results : this.state.projects.concat(response.payload.results),
						showMore,
					});
				}
			} else if (this._isMounted && response.type === types.LOAD_COMPANY_DASHBOARD_PROJECTS_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			if (this._isMounted) Notification('error', 'Error', error);
		} finally {
			if (this._isMounted) this.setState({ isLoading: false });
		}
	};

	handleResize() {
		this.setState({ isMobile: window.innerWidth <= MOBILE_WINDOW_BREAKPOINT });
	}

	onSliceClick = (dataPoint) => {
		const { x: sliceName, projectId, projectName } = dataPoint.data;
		const { groupId: barTitle } = dataPoint;
		this.openDrawer(projectId, projectName, sliceName, barTitle);
	};

	onSliceHover = (obj) => {
		const { z: originalCount, y: normalizedPercent } = obj.bar.data;
		return (
			<div className="tooltip-style">
				<b>{originalCount}</b>
				<span className="percentage-text"> ({normalizedPercent}%)</span>
			</div>
		);
	};

	openDrawer = (projectId, projectName, sliceName, barTitle) => {
		this.setState({
			selectedProject: { id: projectId, name: projectName, sliceName, barTitle },
			isDrawerOpen: true,
		});
	};

	onShowMore = () => {
		this.setState({ pageNumber: this.state.pageNumber + 1 }, this.loadProjects);
	};

	onCompleteDrawer = () => {
		this.setState({ isDrawerOpen: false, selectedProject: null });
	};

	newProject = () => {
		const user = this.props.user;

		if (user.customerRole === CUSTOMER_ROLES.User.name) {
			this.permissionsModal.current.show();
		} else {
			this.props.history.push(Routes.NewProjectWizard);
		}
	};
	
	render() {
		return (
			<div className="projects-widget">
				<div className="row">
					<div className="col-12">
						<div className="dashboard-widget-title">Multi-Team Projects</div>
						<div className="dashboard-widget-subtitle">Projects being implemented by multiple teams</div>
						<div className="d-inline-flex float-end">
							<StatusLegend />
						</div>
					</div>
				</div>
				<LoadingIndicator isLoading={this.state.isLoading}>
					{this.state.projects.length > 0 && (
						<>
							<div className="row mt-3">
								{this.state.projects.map((project, i) => (
									<div className="col-lg-3 col-md-4 col-sm-6 mb-3" key={i}>
										<RadialBarGraph
											title={TruncateText(project.projectName, 35)}
											data={project.projectData}
											onClick={this.onSliceClick}
											onHover={this.onSliceHover}
											user={this.props.user}
											history={this.props.history}
											projectKey={project.projectKey}
											projectId={project.projectId}
											onFilterByProject={this.props.onFilterByProject}
										/>
									</div>
								))}
								{!this.state.isMobile && (
									<div className="col-lg-3 col-md-4 col-sm-6 mb-3">
										<div
											className="pacer-new-project-button"
											onClick={this.newProject}
										>
											<i className="pi pi-plus-circle"></i>
											<span>New Multi-Team Project</span>
										</div>
									</div>
								)}
							</div>
							{this.state.showMore && (
								<div className="d-flex justify-content-center">
									<Button
										type="button"
										text="Show More"
										bootstrapClass="btn btn-link"
										name="showMore"
										onClick={this.onShowMore}
									/>
								</div>
							)}
						</>
					)}
					{!this.state.isLoading && this.state.projects.length === 0 && (
						<div>There are no active projects for this location.</div>
					)}
					<br />
					{this.state.isDrawerOpen && (
						<ProjectsWidgetDrawer
							project={this.state.selectedProject}
							filters={this.props.settings}
							onComplete={this.onCompleteDrawer}
							compDashboardActions={this.props.companyDashboardActions}
						/>
					)}
					<Modal61
						title="Insufficient Permissions"
						body={
							<span>
								This feature is restricted to administrators with <b>{this.props.user.currentSubscriptionName}</b>. Contact your
								administrator to have a project created or{' '}
								<a href="https://www.getpacer.com/contact" target="_blank" rel="noreferrer">
									reach out to Pacer
								</a>{' '}
								to get your own subscription to Pacer
							</span>
						}
						primaryButtonText="Ok"
						secondaryButtonText="Close"
						isCentered={true}
						ref={this.permissionsModal}
					/>
				</LoadingIndicator>
			</div>
		);
	}
}

Projects.propTypes = {
	settings: PropTypes.object,
	onFilterByProject: PropTypes.func.isRequired,
	windowWidth: PropTypes.number.isRequired,
	companyDashboardActions: PropTypes.object.isRequired,
	user: PropTypes.object,
	history: PropTypes.object.isRequired,
	callAbortableApi: PropTypes.func.isRequired,
};

function mapStateToProps(state, ownProps) {
	return {
		windowWidth: state.window.width,
		user: state.user,
		history: ownProps.history,
	};
}

function mapDispatchToProps(dispatch) {
	return {
		companyDashboardActions: bindActionCreators(companyDashboardActions, dispatch),
	};
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withAbortableApi(Projects)));
